import { XMarkIcon } from "@heroicons/react/24/outline";
import { useState } from "react";

interface EMRDeleteReasonModalProps {
  title: string;
  itemName?: string;
  variant?: "treatment" | "diagnostic";
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

const EMRDeleteReasonModal = ({ title, itemName, variant, onConfirm, onClose }: EMRDeleteReasonModalProps) => {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const handleConfirm = () => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setError("Reason is required to delete this item");
      return;
    }
    onConfirm(trimmedReason);
  };

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="fixed inset-0 w-screen h-screen bg-gray-800 opacity-75 transition-opacity" onClick={onClose} />
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left border border-gray-200 transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 w-full overflow-hidden z-10">
          <h3 className="text-base font-semibold leading-6 text-gray-900 flex justify-between items-center mb-4">
            <span>{title}</span>
            <XMarkIcon className="w-4 h-4 cursor-pointer text-gray-400 hover:text-gray-600" onClick={onClose} />
          </h3>
          <div className="mt-2">
            <p className="text-red-500 text-sm font-medium pb-4">
              {variant === "diagnostic" ? (
                <>You are deleting a lab test which has been requested by the customer. Please enter a reason for the same.</>
              ) : variant === "treatment" ? (
                <>
                  You are deleting <strong>{itemName}</strong>. Please enter a reason to continue as this procedure needs to be done post surgery.
                </>
              ) : (
                <>
                  You are deleting <strong>{itemName}</strong>. Please enter a reason to continue.
                </>
              )}
            </p>
            <textarea
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                if (event.target.value.trim()) setError("");
              }}
              rows={2}
              placeholder="Enter reason for deleting this item"
              className="w-full p-2 border border-gray-300 rounded-md text-sm outline-none"
              autoFocus
            />
            {error && <p className="text-xs text-red-500 mt-1 font-medium">{error}</p>}
          </div>
          <div className="mt-5 left-0 sm:mt-4 sm:flex items-center sm:flex-row-reverse gap-1">
            <button
              type="button"
              disabled={!reason.trim()}
              onClick={handleConfirm}
              className="inline-flex w-full justify-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:bg-gray-400 sm:ml-3 sm:w-auto bg-red-600 hover:bg-red-500"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 flex items-center w-full justify-center rounded-md bg-white px-6 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EMRDeleteReasonModal;
